import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { PillButton } from '@sorare/core/src/atoms/buttons/PillButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { iconChevronRight } from '@sorare/core/src/atoms/icons/system/iconChevronRight.ts';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { monetaryAmountFragment } from '@sorare/core/src/lib/monetaryAmount';
import type { StatsRarity } from '@sorare/core/src/lib/playerSearchView';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { css } from '@sorare/css';

import type { MarketValueCell_commonPlayer } from './__generated__/index.graphql.ts';

export const marketValueCell = css`
  width: 100%;
  padding-inline: var(--market-value-padding);
  border-left: 1px solid var(--c-nd-150-opaque);
  @media ${laptopAndAbove} {
    position: sticky;
    background-color: var(--current-row-color);
    right: 0;
    z-index: 1;
  }
`;

/* Shared by the body cell and the header labels so both columns stay aligned. */
export const marketValueColumn = css`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-width: 0;
`;

export type MarketValues = Pick<
  MarketValueCell_commonPlayer,
  | 'limitedMarketValue'
  | 'rareMarketValue'
  | 'superRareMarketValue'
  | 'uniqueMarketValue'
>;

type MarketValueAmountFragment = NonNullable<
  MarketValues['limitedMarketValue']
>;

const MARKET_VALUE_KEY_BY_RARITY = {
  [Rarity.limited]: 'limitedMarketValue',
  [Rarity.rare]: 'rareMarketValue',
  [Rarity.super_rare]: 'superRareMarketValue',
  [Rarity.unique]: 'uniqueMarketValue',
} as const satisfies Record<StatsRarity, keyof MarketValues>;

const marketValueByRarity = (
  marketValues: MarketValues | undefined,
  rarity: StatsRarity
) => marketValues?.[MARKET_VALUE_KEY_BY_RARITY[rarity]];

const MarketValuePill = ({
  monetaryAmount,
  rarity,
  onClick,
}: {
  monetaryAmount: MarketValueAmountFragment;
  rarity: StatsRarity;
  onClick: () => void;
}) => {
  const { main } = useAmountWithConversion({ monetaryAmount });

  return (
    <PillButton
      size="small"
      color="transparent"
      onClick={onClick}
      startIcon={<ScarcityIcon rarity={rarity} />}
      endIcon={<FontAwesomeIcon icon={iconChevronRight} size="sm" />}
    >
      {main}
    </PillButton>
  );
};

type Props = {
  marketValues?: MarketValues;
  rarities: StatsRarity[];
  onListingsClick: (rarity: StatsRarity) => void;
};

export const MarketValueCell = ({
  marketValues,
  rarities,
  onListingsClick,
}: Props) => (
  <Horizontal gap={0.5} className={marketValueCell}>
    {rarities.map(rarity => {
      const marketValue = marketValueByRarity(marketValues, rarity);
      return (
        <div key={rarity} className={marketValueColumn}>
          {!!marketValue && (
            <MarketValuePill
              monetaryAmount={marketValue}
              rarity={rarity}
              onClick={() => onListingsClick(rarity)}
            />
          )}
        </div>
      );
    })}
  </Horizontal>
);

MarketValueCell.fragments = {
  commonPlayer: gql`
    fragment MarketValueCell_commonPlayer on CommonPlayer {
      id
      limitedMarketValue: marketValue(
        rarity: limited
        seasonEligibility: $seasonEligibility
      ) @include(if: $withLimitedMarketValue) {
        ...MonetaryAmountFragment_monetaryAmount
      }
      rareMarketValue: marketValue(
        rarity: rare
        seasonEligibility: $seasonEligibility
      ) @include(if: $withRareMarketValue) {
        ...MonetaryAmountFragment_monetaryAmount
      }
      superRareMarketValue: marketValue(
        rarity: super_rare
        seasonEligibility: $seasonEligibility
      ) @include(if: $withSuperRareMarketValue) {
        ...MonetaryAmountFragment_monetaryAmount
      }
      uniqueMarketValue: marketValue(
        rarity: unique
        seasonEligibility: $seasonEligibility
      ) @include(if: $withUniqueMarketValue) {
        ...MonetaryAmountFragment_monetaryAmount
      }
    }
    ${monetaryAmountFragment}
  ` as TypedDocumentNode<MarketValueCell_commonPlayer>,
};
